import { BaseMapper, Undefinable } from '@ps/utils';
import {
  QualificationDependencyDto,
  SurveyBaseQualification,
  SurveyBaseQualificationDto,
  SurveyQualification,
  SurveyQualificationDependency,
  SurveyQualificationDto,
  SurveyRangeQualification,
  SurveyRangeQualificationDto,
  SurveySelectableQualification,
  SurveySelectableQualificationDto,
  toChildrenLegacyQualification,
  toChildrenLegacyQualificationCode,
} from '@ps/survey-utils';

export class SurveyQualificationMapper extends BaseMapper<
  SurveyQualificationDto,
  SurveyQualification
> {
  public override fromDtoList(
    dtos: SurveyQualificationDto[],
  ): SurveyQualification[] {
    return dtos
      .filter((dto) => !!dto.qualification_code)
      .map((dto) => this.fromDto(dto));
  }

  public fromDto(dto: SurveyQualificationDto): SurveyQualification {
    if (this._isRangeDto(dto)) {
      return this._rangeQualification(dto);
    }

    return this._selectableQualification(dto);
  }

  // The survey-list endpoint omits isRangeType, so a list row's range qualification is only
  // identifiable by the range it carries.
  private _isRangeDto(
    dto: SurveyQualificationDto,
  ): dto is SurveyRangeQualificationDto {
    return dto.isRangeType === 1 || this._carriesRangeSets(dto);
  }

  // Structural: ruling out isRangeType collapses the union to the selectable variant, which
  // declares no range_sets.
  private _carriesRangeSets(
    dto: SurveyBaseQualificationDto & { range_sets?: readonly unknown[] },
  ): boolean {
    return !!dto.range_sets?.length;
  }

  private _baseQualification(
    dto: SurveyQualificationDto,
  ): SurveyBaseQualification {
    return {
      qualificationCode: dto.qualification_code,
      qualificationName: dto.qualification_name,
      key: dto.key,
      isDefault: !!dto.isDefault,
      isAllSet: !!dto.isAllSet,
      isQuotaSet: !!dto.isQuotaSet,
      hasDependency: dto.hasDependency,
      ...this._dependencyFields(dto.dependency),
    };
  }

  private _dependencyFields(
    dependency: Undefinable<QualificationDependencyDto>,
  ): Partial<Pick<SurveyBaseQualification, 'dependency'>> {
    if (!dependency) {
      return {};
    }

    return { dependency: this._toDependency(dependency) };
  }

  private _toDependency(
    dependency: QualificationDependencyDto,
  ): SurveyQualificationDependency {
    return {
      respondentQuestionId: dependency.respondent_question_id,
      parentQualificationId: toChildrenLegacyQualificationCode(
        dependency.parentQualId,
      ),
      matchCriteria: dependency.matchCriteria,
      repeat: dependency.repeat,
      condition: dependency.condition,
    };
  }

  private _rangeQualification(
    dto: SurveyRangeQualificationDto,
  ): SurveyRangeQualification {
    return {
      ...this._baseQualification(dto),
      isRangeType: true,
      rangeSets: (dto.range_sets ?? []).map((rangeSet) => ({
        from: rangeSet.from,
        to: rangeSet.to,
        units: rangeSet.units,
      })),
      data: (dto.data ?? []).map((datum) => ({
        min: datum.mn,
        max: datum.mx,
        units: datum.units,
      })),
    };
  }

  // The wire's own legacy 232 (child-age-gender) qualification code must be normalized back to
  // 218 ("Children") here too, mirroring SurveyQuotaMapper's per-criterion conversion — the local
  // qualification catalog only ever carries 218 (232 is filtered out entirely, see
  // visible-qualifications.helper.ts), so a survey-owned qualification left at 232 fails every
  // code-keyed catalog lookup (surveyQualificationByCode) and silently falls back to
  // buildDefaultSurveyQualification's isQuotaSet: false — hiding the quota's edit/delete icons.
  private _selectableQualification(
    dto: SurveySelectableQualificationDto,
  ): SurveySelectableQualification {
    const { qualificationCode, conditionCodes } = toChildrenLegacyQualification(
      {
        qualificationCode: dto.qualification_code,
        conditionCodes: dto.condition_codes,
      },
    );

    return {
      ...this._baseQualification(dto),
      qualificationCode,
      isRangeType: false,
      conditionCodes,
      data: (dto.data ?? []).map((datum) => ({
        id: datum.id,
        name: datum.name,
      })),
    };
  }
}
